import type {
  CandidateChange,
  FlatStep,
  GeneralReference,
  GeneralReferenceRow,
  OptionalBranch,
  ProcessPhase,
  ProcessStep,
  ProjectReferenceChoice,
  ProjectSnapshot,
  ProjectRow,
  StepState,
  StepStatus,
} from '../types'

const DEFAULT_STATE: StepState = {
  step_id: '',
  status: 'Non commencé',
  truth_state: 'reference_par_defaut',
}

export const statusOptions: StepStatus[] = [
  'Non commencé',
  'En cours',
  'En attente',
  'À confirmer',
  'Terminé',
  'Bloqué',
]

function orderedPhases(phases: ProcessPhase[], includeDisabled = false) {
  return phases
    .filter((phase) => includeDisabled || phase.enabled !== false)
    .map((phase) => ({
      ...phase,
      steps: [...phase.steps]
        .filter((step) => includeDisabled || step.enabled !== false)
        .sort((left, right) => (left.order ?? phase.steps.indexOf(left) + 1) - (right.order ?? phase.steps.indexOf(right) + 1)),
    }))
    .sort((left, right) => left.order - right.order)
}

export type ProcessStructureItem = {
  step: ProcessStep
  phaseId: string
  phaseName: string
  phaseOrder: number
  isSubstep: boolean
  parentStepId?: string
}

export function processStructureItems(phases: ProcessPhase[], includeDisabled = false): ProcessStructureItem[] {
  const items: ProcessStructureItem[] = []
  orderedPhases(phases, includeDisabled).forEach((phase) => {
    phase.steps.forEach((step) => {
      items.push({
        step,
        phaseId: phase.id,
        phaseName: phase.name,
        phaseOrder: phase.order,
        isSubstep: false,
      })
      step.substeps?.forEach((substep) => {
        items.push({
          step: {
            id: substep.id,
            name: substep.name,
            truth_state: substep.truth_state ?? step.truth_state,
            actor_default: step.actor_default,
            expected_output: step.expected_output,
          },
          phaseId: phase.id,
          phaseName: phase.name,
          phaseOrder: phase.order,
          isSubstep: true,
          parentStepId: step.id,
        })
      })
    })
  })
  return items
}

export function phaseStepCounts(phase: ProcessPhase) {
  const items = processStructureItems([phase])
  const primary = items.filter((item) => !item.isSubstep).length
  const nested = items.length - primary
  return { primary, nested, total: items.length }
}

export function processStepCounts(phases: ProcessPhase[]) {
  const items = processStructureItems(phases)
  const primary = items.filter((item) => !item.isSubstep).length
  const nested = items.length - primary
  return { primary, nested, total: items.length }
}

export function processPhases(snapshot: ProjectSnapshot, includeDisabled = false): ProcessPhase[] {
  const phases = snapshot.project.process_phases ?? snapshot.reference.phases
  return orderedPhases(phases, includeDisabled)
}

export function processBranches(snapshot: ProjectSnapshot, includeDisabled = false): OptionalBranch[] {
  const branches = snapshot.project.process_branches ?? snapshot.reference.optional_branches
  return branches.filter((branch) => includeDisabled || branch.enabled !== false)
}

export function materializeProjectProcess(snapshot: ProjectSnapshot): ProjectSnapshot {
  const next = structuredClone(snapshot)
  next.project.process_source = 'project_specific'
  next.project.process_phases = orderedPhases(processPhases(snapshot, true), true).map((phase, phaseIndex) => ({
    ...phase,
    order: phaseIndex + 1,
    enabled: phase.enabled !== false,
    steps: phase.steps.map((step, stepIndex) => ({ ...step, order: stepIndex + 1, enabled: step.enabled !== false })),
  }))
  next.project.process_branches = structuredClone(processBranches(snapshot, true))
  return next
}

export function projectForReference(reference: GeneralReferenceRow, template: ProjectRow): ProjectRow {
  const snapshot = structuredClone(template.snapshot)
  const firstPhase = reference.content.phases.find((phase) => phase.enabled !== false) ?? reference.content.phases[0]
  const firstStep = firstPhase?.steps.find((step) => step.enabled !== false) ?? firstPhase?.steps[0]
  snapshot.reference = structuredClone(reference.content)
  snapshot.as_of_date = reference.content.effective_date
  snapshot.dataset_kind = 'reference_generale'
  snapshot.project = {
    id: `REF-${reference.reference_key}`,
    name: 'Référence générale',
    country: 'Côte d’Ivoire',
    city: 'Abidjan',
    owner: 'Sablux Côte d’Ivoire',
    status_summary: 'Parcours général publié',
    current_phase_id: firstPhase?.id ?? '',
    current_step_id: firstStep?.id ?? '',
    derogation_applicability: 'a_confirmer',
    truth_state: 'reference_par_defaut',
    process_source: 'reference_default',
    step_states: processStructureItems(reference.content.phases).map(({ step }) => ({
      step_id: step.id,
      status: 'Non commencé' as const,
      truth_state: step.truth_state,
    })),
    events: [],
  }
  return {
    id: `reference:${reference.reference_key}`,
    slug: reference.reference_key,
    code: 'REF-GENERALE',
    name: 'Référence générale',
    scenario: 'Référence générale',
    city: 'Abidjan',
    current_phase_id: snapshot.project.current_phase_id,
    current_step_id: snapshot.project.current_step_id,
    status_label: snapshot.project.status_summary,
    as_of_date: reference.content.effective_date,
    version: reference.version,
    snapshot,
    updated_at: reference.updated_at,
  }
}

export type ReferenceDelta = {
  id: string
  kind: 'add' | 'remove' | 'change'
  label: string
  before: string | null
  after: string | null
}

function sameValue(left: unknown, right: unknown) {
  return JSON.stringify(left) === JSON.stringify(right)
}

function valueText(value: unknown) {
  if (value === undefined || value === null || value === '') return null
  if (value === true) return 'Oui'
  if (value === false) return 'Non'
  return String(value)
}

export function referenceDeltas(before: GeneralReference, after: GeneralReference): ReferenceDelta[] {
  const deltas: ReferenceDelta[] = []
  const add = (id: string, kind: ReferenceDelta['kind'], label: string, previous: unknown, next: unknown) => {
    if (sameValue(previous, next)) return
    deltas.push({ id, kind, label, before: valueText(previous), after: valueText(next) })
  }

  ;(['title', 'status', 'effective_date', 'warning'] as const).forEach((field) => {
    const labels = { title: 'Titre de la référence', status: 'Statut de la référence', effective_date: 'Date de référence', warning: 'Précision sur la référence' }
    add(`reference:${field}`, 'change', labels[field], before[field], after[field])
  })

  const beforePhases = new Map(before.phases.map((phase) => [phase.id, phase]))
  const afterPhases = new Map(after.phases.map((phase) => [phase.id, phase]))
  const commonPhaseOrderBefore = before.phases.filter((phase) => afterPhases.has(phase.id)).map((phase) => phase.id)
  const commonPhaseOrderAfter = after.phases.filter((phase) => beforePhases.has(phase.id)).map((phase) => phase.id)
  add('phases:order', 'change', 'Ordre des phases', commonPhaseOrderBefore.join(' → '), commonPhaseOrderAfter.join(' → '))

  after.phases.forEach((phase) => {
    const previous = beforePhases.get(phase.id)
    if (!previous) {
      add(`phase:add:${phase.id}`, 'add', `Ajouter la phase « ${phase.name} »`, null, phase.name)
      return
    }
    add(`phase:name:${phase.id}`, 'change', `Renommer la phase « ${previous.name} »`, previous.name, phase.name)
    add(`phase:enabled:${phase.id}`, 'change', `${phase.name} · phase active`, previous.enabled !== false, phase.enabled !== false)

    const beforeSteps = new Map(previous.steps.map((step) => [step.id, step]))
    const afterSteps = new Map(phase.steps.map((step) => [step.id, step]))
    const commonOrderBefore = previous.steps.filter((step) => afterSteps.has(step.id)).map((step) => step.id)
    const commonOrderAfter = phase.steps.filter((step) => beforeSteps.has(step.id)).map((step) => step.id)
    add(`steps:order:${phase.id}`, 'change', `${phase.name} · ordre des étapes`, commonOrderBefore.join(' → '), commonOrderAfter.join(' → '))

    phase.steps.forEach((step) => {
      const oldStep = beforeSteps.get(step.id)
      if (!oldStep) {
        add(`step:add:${step.id}`, 'add', `Ajouter l’étape « ${step.name} »`, null, step.name)
        return
      }
      ;(['name', 'enabled', 'optional', 'actor_default', 'expected_output', 'note'] as const).forEach((field) => {
        const labels = { name: 'Libellé', enabled: 'Active', optional: 'Facultative', actor_default: 'Interlocuteur', expected_output: 'Résultat attendu', note: 'Précision' }
        const oldValue = field === 'enabled' ? oldStep.enabled !== false : field === 'optional' ? oldStep.optional === true : oldStep[field]
        const newValue = field === 'enabled' ? step.enabled !== false : field === 'optional' ? step.optional === true : step[field]
        add(`step:${field}:${step.id}`, 'change', `${step.name} · ${labels[field]}`, oldValue, newValue)
      })

      const oldSubsteps = new Map((oldStep.substeps ?? []).map((substep) => [substep.id, substep]))
      const newSubsteps = new Map((step.substeps ?? []).map((substep) => [substep.id, substep]))
      const oldCommonOrder = (oldStep.substeps ?? []).filter((substep) => newSubsteps.has(substep.id)).map((substep) => substep.id)
      const newCommonOrder = (step.substeps ?? []).filter((substep) => oldSubsteps.has(substep.id)).map((substep) => substep.id)
      add(`substeps:order:${step.id}`, 'change', `${step.name} · ordre des étapes associées`, oldCommonOrder.join(' → '), newCommonOrder.join(' → '))
      ;(step.substeps ?? []).forEach((substep) => {
        const oldSubstep = oldSubsteps.get(substep.id)
        if (!oldSubstep) add(`substep:add:${substep.id}`, 'add', `Ajouter l’étape associée « ${substep.name} »`, null, substep.name)
        else add(`substep:name:${substep.id}`, 'change', `Renommer l’étape associée « ${oldSubstep.name} »`, oldSubstep.name, substep.name)
      })
      ;(oldStep.substeps ?? []).forEach((substep) => {
        if (!newSubsteps.has(substep.id)) add(`substep:remove:${substep.id}`, 'remove', `Retirer l’étape associée « ${substep.name} »`, substep.name, null)
      })
    })
    previous.steps.forEach((step) => {
      if (!afterSteps.has(step.id)) add(`step:remove:${step.id}`, 'remove', `Retirer l’étape « ${step.name} »`, step.name, null)
    })
  })
  before.phases.forEach((phase) => {
    if (!afterPhases.has(phase.id)) add(`phase:remove:${phase.id}`, 'remove', `Retirer la phase « ${phase.name} »`, phase.name, null)
  })

  const beforeBranches = new Map(before.optional_branches.map((branch) => [branch.id, branch]))
  const afterBranches = new Map(after.optional_branches.map((branch) => [branch.id, branch]))
  after.optional_branches.forEach((branch) => {
    const previous = beforeBranches.get(branch.id)
    if (!previous) {
      add(`branch:add:${branch.id}`, 'add', `Ajouter la branche « ${branch.name} »`, null, branch.name)
      return
    }
    add(`branch:name:${branch.id}`, 'change', `Branche ${branch.name} · libellé`, previous.name, branch.name)
    add(`branch:trigger:${branch.id}`, 'change', `Branche ${branch.name} · déclenchement`, previous.trigger, branch.trigger)
    branch.steps_summary.forEach((step) => {
      if (!previous.steps_summary.includes(step)) add(`branch-step:add:${branch.id}:${step}`, 'add', `Ajouter « ${step} » à la branche ${branch.name}`, null, step)
    })
    previous.steps_summary.forEach((step) => {
      if (!branch.steps_summary.includes(step)) add(`branch-step:remove:${branch.id}:${step}`, 'remove', `Retirer « ${step} » de la branche ${branch.name}`, step, null)
    })
  })
  before.optional_branches.forEach((branch) => {
    if (!afterBranches.has(branch.id)) add(`branch:remove:${branch.id}`, 'remove', `Retirer la branche « ${branch.name} »`, branch.name, null)
  })

  return deltas
}

function mergeFields<T extends object>(before: T, target: T, current: T, excluded: string[] = []): T {
  const next = structuredClone(current) as Record<string, unknown>
  const old = before as Record<string, unknown>
  const wanted = target as Record<string, unknown>
  const now = current as Record<string, unknown>
  const keys = new Set([...Object.keys(old), ...Object.keys(wanted), ...Object.keys(now)])
  keys.forEach((key) => {
    if (excluded.includes(key) || !sameValue(now[key], old[key])) return
    if (Object.prototype.hasOwnProperty.call(wanted, key)) next[key] = structuredClone(wanted[key])
    else delete next[key]
  })
  return next as T
}

function mergeById<T extends { id: string }>(
  before: T[],
  target: T[],
  current: T[],
  merge: (oldItem: T, targetItem: T, currentItem: T) => T,
  protect: (item: T) => boolean = () => false,
  ordered = false,
) {
  const oldMap = new Map(before.map((item) => [item.id, item]))
  const targetMap = new Map(target.map((item) => [item.id, item]))
  const currentMap = new Map(current.map((item) => [item.id, item]))
  const result: T[] = []

  target.forEach((targetItem) => {
    const oldItem = oldMap.get(targetItem.id)
    const currentItem = currentMap.get(targetItem.id)
    if (!oldItem) result.push(structuredClone(currentItem ?? targetItem))
    else if (currentItem) result.push(merge(oldItem, targetItem, currentItem))
  })
  current.forEach((currentItem) => {
    if (targetMap.has(currentItem.id)) return
    const oldItem = oldMap.get(currentItem.id)
    if (!oldItem || protect(currentItem) || !sameValue(currentItem, oldItem)) result.push(structuredClone(currentItem))
  })

  if (!ordered) return result
  const targetOrder = new Map(target.map((item, index) => [item.id, index + 1]))
  const currentOrder = new Map(current.map((item, index) => [item.id, index + 1]))
  return result.sort((left, right) => {
    const leftOrder = (left as { order?: number }).order ?? targetOrder.get(left.id) ?? currentOrder.get(left.id) ?? 999
    const rightOrder = (right as { order?: number }).order ?? targetOrder.get(right.id) ?? currentOrder.get(right.id) ?? 999
    return leftOrder - rightOrder
  })
}

function mergeSubsteps(before: NonNullable<ProcessStep['substeps']>, target: NonNullable<ProcessStep['substeps']>, current: NonNullable<ProcessStep['substeps']>) {
  return mergeById(before, target, current, (oldItem, targetItem, currentItem) => mergeFields(oldItem, targetItem, currentItem))
}

function mergeSteps(before: ProcessStep[], target: ProcessStep[], current: ProcessStep[], currentStepId: string) {
  return mergeById(before, target, current, (oldStep, targetStep, currentStep) => {
    const merged = mergeFields(oldStep, targetStep, currentStep, ['substeps'])
    merged.substeps = mergeSubsteps(oldStep.substeps ?? [], targetStep.substeps ?? [], currentStep.substeps ?? [])
    if (!merged.substeps.length) delete merged.substeps
    return merged
  }, (step) => step.id === currentStepId || Boolean(step.substeps?.some((substep) => substep.id === currentStepId)), true)
}

function mergePhases(before: ProcessPhase[], target: ProcessPhase[], current: ProcessPhase[], currentStepId: string) {
  return mergeById(before, target, current, (oldPhase, targetPhase, currentPhase) => {
    const merged = mergeFields(oldPhase, targetPhase, currentPhase, ['steps'])
    merged.steps = mergeSteps(oldPhase.steps, targetPhase.steps, currentPhase.steps, currentStepId)
    return merged
  }, (phase) => phase.steps.some((step) => step.id === currentStepId || step.substeps?.some((substep) => substep.id === currentStepId)), true)
}

function mergeBranches(before: OptionalBranch[], target: OptionalBranch[], current: OptionalBranch[]) {
  return mergeById(before, target, current, (oldBranch, targetBranch, currentBranch) => {
    const merged = mergeFields(oldBranch, targetBranch, currentBranch, ['steps'])
    if (oldBranch.steps || targetBranch.steps || currentBranch.steps) {
      merged.steps = mergeSteps(oldBranch.steps ?? [], targetBranch.steps ?? [], currentBranch.steps ?? [], '')
    }
    return merged
  })
}

export function referenceUpdateAvailable(snapshot: ProjectSnapshot, target: GeneralReference) {
  if (snapshot.reference.id === target.id) return false
  return !(snapshot.project.reference_decisions ?? []).some((decision) => decision.reference_after_id === target.id)
}

export function applyReferenceDecision(
  source: ProjectSnapshot,
  target: GeneralReference,
  choice: ProjectReferenceChoice,
  resultProjectVersion: number,
): ProjectSnapshot {
  if (!referenceUpdateAvailable(source, target)) throw new Error('Cette version de la référence a déjà fait l’objet d’une décision pour ce projet.')

  const next = structuredClone(source)
  const beforeId = source.reference.id
  if (choice !== 'keep') {
    const currentPhases = processPhases(source, true)
    const currentBranches = processBranches(source, true)
    const mergedPhases = mergePhases(source.reference.phases, target.phases, currentPhases, source.project.current_step_id)
    const mergedBranches = mergeBranches(source.reference.optional_branches, target.optional_branches, currentBranches)
    const hadLocalRoute = Boolean(source.project.process_phases || source.project.process_branches)
    const keepLocalRoute = choice === 'adapt' || hadLocalRoute || !sameValue(mergedPhases, target.phases) || !sameValue(mergedBranches, target.optional_branches)

    next.reference = structuredClone(target)
    if (keepLocalRoute) {
      next.project.process_source = 'project_specific'
      next.project.process_phases = mergedPhases
      next.project.process_branches = mergedBranches
    } else {
      next.project.process_source = 'reference_default'
      delete next.project.process_phases
      delete next.project.process_branches
    }
    const reconciled = reconcileStepStates(next)
    next.project.step_states = reconciled.project.step_states
  }

  const decidedAt = new Date().toISOString()
  const labels: Record<ProjectReferenceChoice, string> = {
    adopt: 'Nouvelle référence utilisée',
    adapt: 'Nouvelle référence utilisée puis adaptée au projet',
    keep: 'Référence actuelle conservée',
  }
  next.project.reference_decisions = [{
    id: crypto.randomUUID(),
    choice,
    reference_before_id: beforeId,
    reference_after_id: target.id,
    decided_at: decidedAt,
    result_project_version: resultProjectVersion,
  }, ...(next.project.reference_decisions ?? [])]
  next.project.events.unshift({
    id: crypto.randomUUID(),
    date: next.as_of_date,
    type: 'decision_reference_projet',
    summary: `${labels[choice]} · ${beforeId} → ${target.id}`,
  })
  return next
}

export function reconcileStepStates(snapshot: ProjectSnapshot): ProjectSnapshot {
  const next = structuredClone(snapshot)
  const states = new Map(next.project.step_states.map((state) => [state.step_id, state]))
  const structuralSteps = processStructureItems(processPhases(next, true), true).map((item) => item.step)
  processBranches(next, true).forEach((branch) => structuralSteps.push(...(branch.steps ?? [])))
  for (const step of structuralSteps) {
    if (!states.has(step.id)) {
      const state = { ...DEFAULT_STATE, step_id: step.id, truth_state: step.truth_state }
      next.project.step_states.push(state)
      states.set(step.id, state)
    }
  }
  return next
}

export function allSteps(snapshot: ProjectSnapshot): FlatStep[] {
  const states = new Map(snapshot.project.step_states.map((state) => [state.step_id, state]))
  return processStructureItems(processPhases(snapshot)).map(({ step, phaseId, phaseName, phaseOrder, isSubstep }) => ({
    ...step,
    phaseId,
    phaseName,
    phaseOrder,
    isSubstep,
    state: states.get(step.id) ?? { ...DEFAULT_STATE, step_id: step.id, truth_state: step.truth_state },
  }))
}

export function phaseProgress(snapshot: ProjectSnapshot, phaseId: string) {
  const phaseSteps = allSteps(snapshot).filter((step) => step.phaseId === phaseId && !step.isSubstep)
  const completed = phaseSteps.filter((step) => step.state.status === 'Terminé').length
  return { completed, total: phaseSteps.length, percent: phaseSteps.length ? (completed / phaseSteps.length) * 100 : 0 }
}

export function currentStep(snapshot: ProjectSnapshot) {
  return allSteps(snapshot).find((step) => step.id === snapshot.project.current_step_id)
}

export function formatDate(value?: string) {
  if (!value) return '—'
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(`${value}T12:00:00`))
}

const DAY_MS = 86_400_000

function isoDay(value?: string) {
  if (!value) return undefined
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return undefined
  return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
}

export function differenceInCalendarDays(start?: string, end?: string) {
  const startDay = isoDay(start)
  const endDay = isoDay(end)
  if (startDay === undefined || endDay === undefined) return undefined
  return Math.round((endDay - startDay) / DAY_MS)
}

export function differenceInBusinessDays(start?: string, end?: string): number | undefined {
  const startDay = isoDay(start)
  const endDay = isoDay(end)
  if (startDay === undefined || endDay === undefined) return undefined
  if (startDay === endDay) return 0
  if (endDay < startDay) {
    const reversed = differenceInBusinessDays(end, start)
    return reversed === undefined ? undefined : -reversed
  }
  let count = 0
  for (let cursor = startDay + DAY_MS; cursor <= endDay; cursor += DAY_MS) {
    const weekday = new Date(cursor).getUTCDay()
    if (weekday !== 0 && weekday !== 6) count += 1
  }
  return count
}

export type StepTemporalMetrics = {
  anchorDate?: string
  elapsedDays?: number
  effectiveElapsedDays?: number
  remainingDays?: number
  delayDays?: number
  varianceDays?: number
  deadlineDate?: string
  deadlineDeltaDays?: number
}

export function stepTemporalMetrics(state: StepState, asOfDate: string): StepTemporalMetrics {
  const unit = state.reference_duration_unit ?? 'calendaires'
  const diff = unit === 'ouvrés' ? differenceInBusinessDays : differenceInCalendarDays
  const anchorDate = state.actual_start_date ?? state.status_since ?? state.forecast_start_date ?? state.baseline_start_date
  const endDate = state.completed_date ?? asOfDate
  const elapsedDays = anchorDate ? diff(anchorDate, endDate) : undefined
  const effectiveElapsedDays = elapsedDays === undefined
    ? undefined
    : Math.max(0, elapsedDays - Math.max(0, state.paused_days ?? 0))
  const remainingDays = state.reference_duration_days === undefined || effectiveElapsedDays === undefined
    ? undefined
    : state.reference_duration_days - effectiveElapsedDays
  const comparisonEnd = state.completed_date ?? state.forecast_end_date
  const varianceDays = state.baseline_end_date && comparisonEnd
    ? differenceInCalendarDays(state.baseline_end_date, comparisonEnd)
    : undefined
  const deadlineDate = state.forecast_end_date ?? state.baseline_end_date
  const deadlineDeltaDays = !state.completed_date && deadlineDate
    ? differenceInCalendarDays(asOfDate, deadlineDate)
    : undefined

  return {
    anchorDate,
    elapsedDays,
    effectiveElapsedDays,
    remainingDays,
    delayDays: remainingDays !== undefined && remainingDays < 0 ? Math.abs(remainingDays) : undefined,
    varianceDays,
    deadlineDate,
    deadlineDeltaDays,
  }
}

export function truthLabel(value: string) {
  const labels: Record<string, string> = {
    reference_par_defaut: 'Référence par défaut',
    confirme_pour_projet: 'Confirmé pour le projet',
    particularite_projet: 'Particularité du projet',
    a_confirmer: 'À confirmer',
    prototype_fictif: 'Projet fictif',
  }
  return labels[value] ?? value
}

export function applyCandidateChanges(
  source: ProjectSnapshot,
  candidates: CandidateChange[],
): ProjectSnapshot {
  const next = structuredClone(source)

  if (candidates.some((item) => item.accepted && item.kind === 'structure')) {
    const materialized = materializeProjectProcess(next)
    next.project.process_source = materialized.project.process_source
    next.project.process_phases = materialized.project.process_phases
    next.project.process_branches = materialized.project.process_branches
  }

  for (const candidate of candidates.filter((item) => item.accepted)) {
    if (candidate.kind === 'structure') {
      applyStructureCandidate(next, candidate)
      continue
    }
    if (candidate.field === 'as_of_date') {
      next.as_of_date = String(candidate.returned)
      continue
    }

    const state = next.project.step_states.find((item) => item.step_id === candidate.stepId)
    if (!state) continue

    if (candidate.field === 'status') state.status = candidate.returned as StepStatus
    if (candidate.field === 'completed_date') state.completed_date = candidate.returned ? String(candidate.returned) : undefined
    if (candidate.field === 'next_action') state.next_action = candidate.returned ? String(candidate.returned) : undefined
    if (candidate.field === 'note') state.note = candidate.returned ? String(candidate.returned) : undefined
  }

  const reconciled = reconcileStepStates(next)
  next.project.process_phases = reconciled.project.process_phases
  next.project.process_branches = reconciled.project.process_branches
  next.project.step_states = reconciled.project.step_states

  next.project.step_states.forEach((state) => {
    if (state.status_since && state.status === 'En attente') {
      const start = new Date(`${state.status_since}T12:00:00`)
      const end = new Date(`${next.as_of_date}T12:00:00`)
      state.waiting_days = Math.max(0, Math.round((end.getTime() - start.getTime()) / 86_400_000))
    }
  })

  const current = currentStep(next)
  if (current) {
    next.project.status_summary = current.state.next_action || `${current.name} — ${current.state.status}`
  }

  return next
}

function applyStructureCandidate(snapshot: ProjectSnapshot, candidate: CandidateChange) {
  if (!snapshot.project.process_phases) {
    const materialized = materializeProjectProcess(snapshot)
    snapshot.project.process_source = materialized.project.process_source
    snapshot.project.process_phases = materialized.project.process_phases
    snapshot.project.process_branches = materialized.project.process_branches
  }
  const phases = snapshot.project.process_phases ?? []

  if (candidate.operation === 'update_derogation') {
    snapshot.project.derogation_applicability = candidate.returned as ProjectSnapshot['project']['derogation_applicability']
    return
  }
  if (candidate.operation === 'add_phase' && candidate.payload) {
    phases.push(structuredClone(candidate.payload as ProcessPhase))
    return
  }
  const phase = phases.find((item) => item.id === (candidate.phaseId ?? candidate.entityId))
  if (candidate.operation === 'remove_phase') {
    const index = phases.findIndex((item) => item.id === candidate.entityId)
    if (index >= 0) phases.splice(index, 1)
    return
  }
  if (!phase) return
  if (candidate.operation === 'add_step' && candidate.payload) {
    phase.steps.push(structuredClone(candidate.payload as ProcessStep))
    return
  }
  if (candidate.operation === 'remove_step') {
    const index = phase.steps.findIndex((step) => step.id === candidate.entityId)
    if (index >= 0) phase.steps.splice(index, 1)
    return
  }
  if (candidate.operation === 'update_phase') {
    if (candidate.field === 'name') phase.name = String(candidate.returned)
    if (candidate.field === 'order') phase.order = Number(candidate.returned)
    if (candidate.field === 'enabled') phase.enabled = Boolean(candidate.returned)
    return
  }
  const step = phase.steps.find((item) => item.id === candidate.entityId)
  if (!step) return
  if (candidate.field === 'name') step.name = String(candidate.returned)
  if (candidate.field === 'order') step.order = Number(candidate.returned)
  if (candidate.field === 'enabled') step.enabled = Boolean(candidate.returned)
  if (candidate.field === 'optional') step.optional = Boolean(candidate.returned)
  if (candidate.field === 'actor_default') step.actor_default = candidate.returned ? String(candidate.returned) : undefined
  if (candidate.field === 'expected_output') step.expected_output = candidate.returned ? String(candidate.returned) : undefined
}

export function structureCandidates(
  before: ProjectSnapshot,
  returned: ProjectSnapshot,
  current: ProjectSnapshot,
  accepted = false,
): CandidateChange[] {
  const changes: CandidateChange[] = []
  const beforePhases = processPhases(before, true)
  const returnedPhases = processPhases(returned, true)
  const currentPhases = processPhases(current, true)
  const currentPhaseMap = new Map(currentPhases.map((phase) => [phase.id, phase]))
  const beforePhaseMap = new Map(beforePhases.map((phase) => [phase.id, phase]))

  const push = (change: Omit<CandidateChange, 'id' | 'kind' | 'accepted' | 'conflict'>) => {
    if (change.before === change.returned || change.current === change.returned) return
    changes.push({
      ...change,
      id: crypto.randomUUID(),
      kind: 'structure',
      accepted,
      conflict: change.current !== change.before,
    })
  }

  for (const phase of returnedPhases) {
    const base = beforePhaseMap.get(phase.id)
    const now = currentPhaseMap.get(phase.id)
    if (!base) {
      push({ path: `project.process_phases.${phase.id}`, label: `Ajouter la phase « ${phase.name} »`, field: 'phase', entityId: phase.id, operation: 'add_phase', payload: phase, before: null, returned: phase.name, current: now?.name ?? null })
      continue
    }
    ;(['name', 'order', 'enabled'] as const).forEach((field) => {
      const baseValue = field === 'enabled' ? base.enabled !== false : base[field]
      const returnedValue = field === 'enabled' ? phase.enabled !== false : phase[field]
      const nowValue = now ? (field === 'enabled' ? now.enabled !== false : now[field]) : null
      push({ path: `project.process_phases.${phase.id}.${field}`, label: `${phase.name} · ${field === 'name' ? 'Nom' : field === 'order' ? 'Ordre' : 'Active'}`, field, entityId: phase.id, phaseId: phase.id, operation: 'update_phase', before: baseValue, returned: returnedValue, current: nowValue })
    })
    const baseSteps = new Map(base.steps.map((step) => [step.id, step]))
    const nowSteps = new Map((now?.steps ?? []).map((step) => [step.id, step]))
    for (const step of phase.steps) {
      const baseStep = baseSteps.get(step.id)
      const nowStep = nowSteps.get(step.id)
      if (!baseStep) {
        push({ path: `project.process_phases.${phase.id}.steps.${step.id}`, label: `Ajouter l’étape « ${step.name} »`, field: 'step', entityId: step.id, phaseId: phase.id, operation: 'add_step', payload: step, before: null, returned: step.name, current: nowStep?.name ?? null })
        continue
      }
      ;(['name', 'order', 'enabled', 'optional', 'actor_default', 'expected_output'] as const).forEach((field) => {
        const baseValue = field === 'enabled' ? baseStep.enabled !== false : field === 'optional' ? baseStep.optional === true : field === 'order' ? baseStep.order ?? base.steps.indexOf(baseStep) + 1 : baseStep[field] ?? null
        const returnedValue = field === 'enabled' ? step.enabled !== false : field === 'optional' ? step.optional === true : field === 'order' ? step.order ?? phase.steps.indexOf(step) + 1 : step[field] ?? null
        const nowValue = nowStep ? (field === 'enabled' ? nowStep.enabled !== false : field === 'optional' ? nowStep.optional === true : field === 'order' ? nowStep.order ?? (now?.steps.indexOf(nowStep) ?? 0) + 1 : nowStep[field] ?? null) : null
        push({ path: `project.process_phases.${phase.id}.steps.${step.id}.${field}`, label: `${step.name} · ${field === 'name' ? 'Nom' : field === 'order' ? 'Ordre' : field === 'enabled' ? 'Active' : field === 'optional' ? 'Facultative' : field === 'actor_default' ? 'Interlocuteur' : 'Résultat attendu'}`, field, entityId: step.id, phaseId: phase.id, operation: 'update_step', before: baseValue, returned: returnedValue, current: nowValue })
      })
    }
    for (const step of base.steps) {
      if (!phase.steps.some((item) => item.id === step.id)) push({ path: `project.process_phases.${phase.id}.steps.${step.id}`, label: `Retirer l’étape « ${step.name} »`, field: 'step', entityId: step.id, phaseId: phase.id, operation: 'remove_step', before: step.name, returned: null, current: nowSteps.get(step.id)?.name ?? null })
    }
  }
  for (const phase of beforePhases) {
    if (!returnedPhases.some((item) => item.id === phase.id)) push({ path: `project.process_phases.${phase.id}`, label: `Retirer la phase « ${phase.name} »`, field: 'phase', entityId: phase.id, operation: 'remove_phase', before: phase.name, returned: null, current: currentPhaseMap.get(phase.id)?.name ?? null })
  }
  if (before.project.derogation_applicability !== returned.project.derogation_applicability) {
    push({ path: 'project.derogation_applicability', label: 'Dérogation · Applicabilité', field: 'derogation_applicability', operation: 'update_derogation', before: before.project.derogation_applicability, returned: returned.project.derogation_applicability, current: current.project.derogation_applicability })
  }
  return changes
}

export function updateStep(
  source: ProjectSnapshot,
  stepId: string,
  update: Partial<Pick<StepState,
    | 'status'
    | 'baseline_start_date'
    | 'baseline_end_date'
    | 'forecast_start_date'
    | 'forecast_end_date'
    | 'actual_start_date'
    | 'completed_date'
    | 'reference_duration_days'
    | 'reference_duration_unit'
    | 'paused_days'
    | 'next_action'
    | 'note'
  >>,
  eventSummary: string,
): ProjectSnapshot {
  const next = structuredClone(source)
  const state = next.project.step_states.find((item) => item.step_id === stepId)
  if (!state) throw new Error(`Étape inconnue : ${stepId}`)

  Object.assign(state, update)
  if (update.status === 'Terminé' && !state.completed_date) state.completed_date = next.as_of_date
  if (update.status && update.status !== 'Terminé') state.completed_date = update.completed_date

  next.project.current_step_id = stepId
  const step = allSteps(next).find((item) => item.id === stepId)
  if (step) next.project.current_phase_id = step.phaseId
  next.project.status_summary = update.next_action || `${step?.name ?? stepId} — ${state.status}`
  next.project.events.unshift({
    id: crypto.randomUUID(),
    date: next.as_of_date,
    type: 'mise_a_jour',
    step_id: stepId,
    summary: eventSummary,
  })

  return next
}

export function updateStepFromTable(
  source: ProjectSnapshot,
  stepId: string,
  update: Partial<Pick<StepState,
    | 'status'
    | 'baseline_start_date'
    | 'baseline_end_date'
    | 'forecast_start_date'
    | 'forecast_end_date'
    | 'actual_start_date'
    | 'completed_date'
    | 'reference_duration_days'
    | 'reference_duration_unit'
    | 'paused_days'
    | 'next_action'
    | 'note'
  >>,
  fieldLabel: string,
): ProjectSnapshot {
  const next = structuredClone(source)
  const state = next.project.step_states.find((item) => item.step_id === stepId)
  if (!state) throw new Error(`Étape inconnue : ${stepId}`)

  const previousStatus = state.status
  Object.assign(state, update)

  if (update.status) {
    if (update.status === 'Terminé') {
      state.completed_date ??= next.as_of_date
      state.status_since = undefined
      state.waiting_days = undefined
    } else {
      state.completed_date = undefined
      if (update.status === 'En attente') {
        if (previousStatus !== 'En attente') state.status_since = next.as_of_date
        state.waiting_days = state.status_since
          ? Math.max(0, Math.round((new Date(`${next.as_of_date}T12:00:00`).getTime() - new Date(`${state.status_since}T12:00:00`).getTime()) / 86_400_000))
          : 0
      } else {
        state.status_since = undefined
        state.waiting_days = undefined
      }
    }
  }

  const step = allSteps(next).find((item) => item.id === stepId)
  if (stepId === next.project.current_step_id) {
    next.project.status_summary = state.next_action || `${step?.name ?? stepId} — ${state.status}`
  }
  next.project.events.unshift({
    id: crypto.randomUUID(),
    date: next.as_of_date,
    type: 'mise_a_jour_tableau',
    step_id: stepId,
    summary: `Tableau de suivi · ${step?.name ?? stepId} · ${fieldLabel}`,
  })

  return next
}
